import { sortBy } from 'lodash-es';
import Ordering from '@/common/constants/ordering.constants';
import type { Flavor } from '@/common/store/pinia/computeFlavors/computeFlavorsMapper';

function severitySort(a: string, b: string): Ordering {
  const sortOrder = ['error', 'warning', 'info'];
  if (sortOrder.indexOf(a) > sortOrder.indexOf(b)) return Ordering.Greater;
  if (sortOrder.indexOf(a) < sortOrder.indexOf(b)) return Ordering.Less;
  return Ordering.Equal;
}

function formattedValueSort(a, b, formatter: (_) => number | string): Ordering {
  const fa = formatter(a);
  const fb = formatter(b);

  if (!fa && !fb) return Ordering.Equal;
  if (!fa) return Ordering.Less;
  if (!fb) return Ordering.Greater;

  if (fa > fb) return Ordering.Greater;
  if (fa < fb) return Ordering.Less;
  return Ordering.Equal;
}

function redundancySorter(a: Redundancy, b: Redundancy): Ordering {
  /*
  Rules:
  1. Replication first, EC - second, empty/null redundancy - third
  2. For replication - sort from low replicas to higher replicas
  3. For EC sort from low number of blocks to higher number of blocks.

  So the sorting should be the following:
  Replicas - no redundancy
  Replicas = 2
  Replicas = 3
  EC - no redundancy
  EC - 1+2
  EC - 3+2
  EC - 5+2
  EC - 7+2
  EC - 17+3
  NULL
   */

  if (a && b) {
    if (a.type !== b.type) {
      return a.type > b.type ? Ordering.Less : Ordering.Greater;
    }

    return (b.m + (b.n || Ordering.Equal)) - (a.m + (a.n || Ordering.Equal));
  }

  if (!a && b) {
    return Ordering.Less;
  }

  if (a && !b) {
    return Ordering.Greater;
  }

  return Ordering.Equal;
}

function sortQuotes(a: { quota: Quota }, b: { quota: Quota }): Ordering {
  const aFreeSpace = (a.quota.limit === -1 ? Infinity : a.quota.limit) - a.quota.used;
  const bFreeSpace = (b.quota.limit === -1 ? Infinity : b.quota.limit) - b.quota.used;
  if (aFreeSpace > bFreeSpace) {
    return Ordering.Greater;
  }
  if (aFreeSpace < bFreeSpace) {
    return Ordering.Less;
  }
  return Ordering.Equal;
}

function speedsSort(a: NodesInterface, b: NodesInterface): Ordering {
  const aIsna = !a.speeds?.max;
  const bIsna = !b.speeds?.max;

  if (aIsna && !bIsna) {
    return -1;
  }
  if (bIsna && !aIsna) {
    return 1;
  }

  if (aIsna && bIsna) {
    return 0;
  }

  if (a.speeds.max && b.speeds.max && a.speeds.max < b.speeds.max) {
    return Ordering.Less;
  }

  if (a.speeds.max && b.speeds.max && a.speeds.max > b.speeds.max) {
    return Ordering.Greater;
  }

  if (a.speeds.current && b.speeds.current && a.speeds.current < b.speeds.current) {
    return Ordering.Less;
  }

  if (a.speeds.current && b.speeds.current && a.speeds.current > b.speeds.current) {
    return Ordering.Greater;
  }

  return Ordering.Equal;
}

function sortMigrateNode(a, b): number {
  if (a.node.isPrimary) {
    return Ordering.Less;
  }

  if (b.node.isPrimary) {
    return Ordering.Greater;
  }
  return a.label.localeCompare(b.label);
}

function sortFlavors(flavors: Flavor[]): Flavor[] {
  return sortBy(flavors, ['vcpus', 'ram', 'name']);
}

export {
  formattedValueSort,
  redundancySorter,
  sortQuotes,
  severitySort,
  speedsSort,
  sortMigrateNode,
  sortFlavors
};
